/**
 * v133 — Photo-Anchored Logging calibration math.
 *
 * Pure functions converting between photo pixel coordinates and inches from
 * the target POA, using the 2-point calibration documented on
 * `PhotoCalibration` in `v3-schema.ts`.
 *
 * Design notes
 * ────────────
 * The math is linear and axis-aligned. We do NOT model:
 *   - photo rotation (user is asked to take the photo square + level)
 *   - perspective skew (oblique camera angles produce ellipse distortion)
 *   - lens barrel distortion (negligible at the target distances most users
 *     shoot from with a modern phone wide-angle)
 * These are all v134 candidates if Sal sees Stage 1 working well enough to
 * justify the bundle cost. The 1-step calibration loop (bull tap + 8-ring
 * tap) was chosen for speed; using a single scale is honest at the precision
 * archers care about (≈0.05" / 1 mm) when the photo is taken square.
 *
 * `pxPerInch` derivation
 * ────────────────────────
 * Given the two taps:
 *   pxPerInch = distance(bullCenterPx, calibPointPx) / calibScoringRingIn
 *
 * Conversion:
 *   inches = (px - bullCenterPx) / pxPerInch
 *   px     = bullCenterPx + inches × pxPerInch
 *
 * Pixel Y grows downward in image space (standard screen convention), which
 * already matches our canonical "down = positive Y" coordinate system on
 * RichShot.hitYIn. No sign flip needed.
 */

import type { PhotoCalibration } from './v3-schema';

/** Euclidean distance between two points in any consistent unit. */
export function distance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * Derived px-per-inch scale from a calibration. Throws if the user tapped
 * the same point twice (zero distance), which is operator error — the caller
 * should require the two calibration taps to be visibly distinct before
 * letting the user proceed.
 */
export function pxPerInch(calib: PhotoCalibration): number {
  const px = distance(calib.bullCenterPx, calib.calibPointPx);
  if (px <= 0) {
    throw new Error(
      'Calibration points are identical — cannot derive scale. Tap two distinct points.',
    );
  }
  return px / calib.calibScoringRingIn;
}

/** Convert a photo-pixel coordinate to inches from POA. */
export function pxToInches(
  px: { x: number; y: number },
  calib: PhotoCalibration,
): { xIn: number; yIn: number } {
  const scale = pxPerInch(calib);
  return {
    xIn: (px.x - calib.bullCenterPx.x) / scale,
    yIn: (px.y - calib.bullCenterPx.y) / scale,
  };
}

/** Convert a (xIn, yIn) inch-from-POA coordinate to photo-pixel coordinates. */
export function inchesToPx(
  inches: { xIn: number; yIn: number },
  calib: PhotoCalibration,
): { x: number; y: number } {
  const scale = pxPerInch(calib);
  return {
    x: calib.bullCenterPx.x + inches.xIn * scale,
    y: calib.bullCenterPx.y + inches.yIn * scale,
  };
}

/**
 * Whether two calibration taps are far enough apart to give a usable scale.
 * Minimum 40 px so a sloppy double-tap doesn't lock in a wildly wrong scale.
 */
export function isCalibrationUsable(calib: Pick<PhotoCalibration, 'bullCenterPx' | 'calibPointPx'>): boolean {
  return distance(calib.bullCenterPx, calib.calibPointPx) >= 40;
}

/** Common scoring-ring radii in inches for the calibration-point picker. */
export const CALIB_RING_PRESETS: ReadonlyArray<{ label: string; radiusIn: number }> = [
  { label: '8-ring (40 cm face)', radiusIn: 4.0 },
  { label: '9-ring (40 cm face)', radiusIn: 3.2 },
  { label: '10-ring (40 cm face)', radiusIn: 2.4 },
  { label: 'X-ring (40 cm face)', radiusIn: 0.4 },
  { label: 'Block 18 cm face', radiusIn: 3.5 },
  { label: '3D vital ring', radiusIn: 4.0 },
];
